/* eslint-disable @typescript-eslint/no-misused-promises */
/**
 * @file AI Gallery 发布弹窗 — 现代化表单控件（标签选择器、访问权限选择器）。
 * 
 * API 字段规范：
 * - tags: TagRef[] 1-5 个标签，元素 {id: 1..9}
 * - visibility: enum (self / user / public)
 * - share_users: string[] visibility=user 时至少 1 个 openid
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSetState } from 'ahooks';
import classNames from 'classnames';
import {
  Bubble,
  Button,
  Dropdown,
  Icon,
  List,
  Radio,
  SearchBox,
  Tag,
  TagSelect,
  StatusTip,
} from 'tea-component';
import { useIntl, getLocale } from 'umi';
import iconTagAdd from '@/pages/aigc/assets/svg/icon-tag-add.svg';
import iconAccessGlobe from '@/pages/aigc/assets/svg/icon-globe.svg';
import iconAccessGlobeActive from '@/pages/aigc/assets/svg/icon-globe-access-active.svg';
import iconAccessPeople from '@/pages/aigc/assets/svg/icon-users.svg';
import iconAccessPeopleActive from '@/pages/aigc/assets/svg/icon-group-access-active.svg';
import iconAccessLock from '@/pages/aigc/assets/svg/icon-lock-access.svg';
import iconAccessLockActive from '@/pages/aigc/assets/svg/icon-lock.svg';
import {
  AI_GALLERY_PUBLISH_MAX_TAGS,
  normalizeAiGalleryPublishVisibilityPayload,
  type TAiGalleryPublishVisibility,
  type IAiGalleryPublishVisibilityPayload,
  type TAiGalleryDataMode,
} from './constants';
import type { IAiGalleryTagItem, IAiGalleryUserItem } from '../../hooks/useAiGalleryPublishApi';

// 重新导出类型和函数，保持向后兼容
export { normalizeAiGalleryPublishVisibilityPayload } from './constants';
export type { IAiGalleryPublishVisibilityPayload } from './constants';

/**
 * UseFormSearch Item 注入的标签字段接口
 * 
 * 注意：value 存储的是标签 ID（number[]），而非标签名称
 */
export interface IAiGalleryPublishTagsFieldProps {
  /** 已选标签 ID 列表 */
  value?: number[];
  /** 变更回调 */
  onChange?: (tagIds: number[]) => void;
  /** 标签选项列表（从 API 获取），若不传则使用预设标签 */
  tagOptions?: IAiGalleryTagItem[];
  /** 标签加载中 */
  tagsLoading?: boolean;
  /** 创建自定义标签回调，返回新标签的 ID（用于自动选中） */
  onCreateTag?: (params: { name_cn: string; name_en: string; }) => Promise<number | null>;
  /** 创建标签加载中 */
  createTagLoading?: boolean;
}

/**
 * 标签选择器：预设标签 + 自定义标签，最多 AI_GALLERY_PUBLISH_MAX_TAGS 个
 * 
 * 注意：存储的是标签 ID（number[]），UI 显示标签名称
 */
export const AiGalleryPublishTagsField: React.FC<IAiGalleryPublishTagsFieldProps> = ({
  value = [],
  onChange,
  tagOptions = [],
  tagsLoading = false,
  onCreateTag,
  createTagLoading = false,
}) => {
  const { formatMessage } = useIntl();
  const selectedTagIds = Array.isArray(value) ? value : [];
  const availableTags = tagOptions;
  
  // 判断当前语言环境
  const isZhLocale = getLocale().startsWith('zh');
  
  // 中文标签最大 10 字，英文标签最大 20 字
  const TAG_CN_MAX_LENGTH = 10;
  const TAG_EN_MAX_LENGTH = 20;

  // 根据 ID 获取标签名称（根据语言环境返回中英文）
  const getTagNameById = useCallback(
    (id: number): string => {
      const tag = availableTags.find(t => t.id === id);
      if (!tag) return `Tag-${id}`;
      return isZhLocale ? (tag.name_cn || tag.name) : (tag.name_en || tag.name);
    },
    [availableTags, isZhLocale],
  );

  // 根据标签对象获取显示名称（根据语言环境返回中英文）
  const getTagDisplayName = useCallback(
    (tag: { name?: string; name_cn?: string; name_en?: string; }): string => {
      return isZhLocale ? (tag.name_cn || tag.name || '') : (tag.name_en || tag.name || '');
    },
    [isZhLocale],
  );

  // 防止 onChange 引用变化导致 Dropdown 死循环
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const [customTagDraft, setCustomTagDraft] = useSetState({ cn: '', en: '' });
  const [showCustomTagInput, setShowCustomTagInput] = useState(false);

  const applyTagIds = useCallback((nextTagIds: number[]): void => {
    onChangeRef.current?.(nextTagIds);
  }, []);

  const toggleTag = useCallback(
    (tagId: number, checked: boolean): void => {
      if (checked) {
        if (selectedTagIds.includes(tagId)) return;
        if (selectedTagIds.length >= AI_GALLERY_PUBLISH_MAX_TAGS) {
          return;
        }
        applyTagIds([...selectedTagIds, tagId]);
      } else {
        applyTagIds(selectedTagIds.filter(id => id !== tagId));
      }
    },
    [applyTagIds, selectedTagIds],
  );

  const addCustomTag = useCallback(async (): Promise<void> => {
    const tagNameCn = customTagDraft.cn.trim();
    const tagNameEn = customTagDraft.en.trim();
    setCustomTagDraft({ cn: '', en: '' });
    setShowCustomTagInput(false);
    
    // 中英文标签名称都需要填写
    if (!tagNameCn || !tagNameEn) return;
    
    // 查找是否已有同名标签（按中文名查找）
    const existingTag = availableTags.find(t => t.name === tagNameCn);
    if (existingTag) {
      if (!selectedTagIds.includes(existingTag.id)) {
        if (selectedTagIds.length >= AI_GALLERY_PUBLISH_MAX_TAGS) {
          return;
        }
        applyTagIds([...selectedTagIds, existingTag.id]);
      }
      return;
    }
    
    // 调用 API 创建自定义标签
    if (!onCreateTag) {
      console.warn('[AiGalleryPublishTagsField] onCreateTag 未传入，无法创建自定义标签');
      return;
    }

    // 检查标签数量限制
    if (selectedTagIds.length >= AI_GALLERY_PUBLISH_MAX_TAGS) {
      return;
    }

    const newTagId = await onCreateTag({
      name_cn: tagNameCn,
      name_en: tagNameEn,
    });

    if (newTagId) {
      // 创建成功，将新标签加入已选列表
      applyTagIds([...selectedTagIds, newTagId]);
    }
  }, [applyTagIds, availableTags, customTagDraft, onCreateTag, selectedTagIds]);

  // 标签下拉触发器
  const tagsDropdownTrigger = useMemo(() => (
    <div className="dba-ai-gallery-publish-modal__tags-trigger">
      {selectedTagIds.length === 0 ? (
        <span className="dba-ai-gallery-publish-modal__placeholder">
          {formatMessage({
            id: 'dba_ai_gallery_publish_tags_placeholder',
          })}
        </span>
      ) : (
        <Tag.Group>
          {selectedTagIds.map(tagId => (
            <Tag
              key={tagId}
              onClose={(e) => {
                e.stopPropagation();
                applyTagIds(selectedTagIds.filter(id => id !== tagId));
              }}
            >
              {getTagNameById(tagId)}
            </Tag>
          ))}
        </Tag.Group>
      )}
      <Icon type="arrowdown" />
    </div>
  ), [applyTagIds, formatMessage, getTagNameById, selectedTagIds]);

  return (
    <div className="dba-ai-gallery-publish-modal__tags-field-root">
      <Dropdown
        trigger="click"
        clickClose={false}
        placement="bottom-start"
        appearance="pure"
        matchButtonWidth
        updateOnChildrenChange
        overlayClassName="dba-ai-gallery-publish-modal__tags-overlay"
        button={tagsDropdownTrigger}
      >
        <div className="dba-ai-gallery-publish-modal__tags-panel">
          {!showCustomTagInput ? (
            <>
              <div className="dba-ai-gallery-publish-modal__tags-list-wrap">
                {tagsLoading ? (
                  <div className="dba-ai-gallery-publish-modal__tags-loading">
                    {formatMessage({ id: 'dba_loading' })}
                  </div>
                ) : (
                  <div className="dba-ai-gallery-publish-modal__tags-checks">
                    {availableTags.map(tag => {
                      const isSelected = selectedTagIds.includes(tag.id);
                      const isMaxReached = selectedTagIds.length >= AI_GALLERY_PUBLISH_MAX_TAGS;
                      const isDisabled = isMaxReached && !isSelected;
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          className={classNames('dba-ai-gallery-publish-modal__preset-tag-btn', {
                            'dba-ai-gallery-publish-modal__preset-tag-btn--on': isSelected,
                            'dba-ai-gallery-publish-modal__preset-tag-btn--disabled': isDisabled,
                          })}
                          disabled={isDisabled}
                          onClick={() => toggleTag(tag.id, !isSelected)}
                        >
                          <span
                            className="dba-ai-gallery-publish-modal__preset-tag-check"
                            aria-hidden={true}
                          />
                          {getTagDisplayName(tag)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="dba-ai-gallery-publish-modal__tags-footer">
                <Bubble
                  content={selectedTagIds.length >= AI_GALLERY_PUBLISH_MAX_TAGS
                    ? formatMessage({ id: 'dba_ai_gallery_publish_tags_max_tip' })
                    : null}
                  overlayClassName="dba-nav-menu-overlay dba-ai-gallery-publish-modal__bubble-top"
                >
                  <button
                    type="button"
                    className={classNames('dba-ai-gallery-publish-modal__tag-add-btn', {
                      'dba-ai-gallery-publish-modal__tag-add-btn--disabled': selectedTagIds.length >= AI_GALLERY_PUBLISH_MAX_TAGS,
                    })}
                    disabled={selectedTagIds.length >= AI_GALLERY_PUBLISH_MAX_TAGS}
                    onClick={() => setShowCustomTagInput(true)}
                  >
                    <img
                      src={iconTagAdd}
                      alt=""
                      className="dba-ai-gallery-publish-modal__tag-add-icon"
                    />
                    {formatMessage({ id: 'dba_ai_gallery_publish_tag_add' })}
                  </button>
                </Bubble>
              </div>
            </>
          ) : (
            <div className="dba-ai-gallery-publish-modal__tags-create-panel">
              {/* 中文标签名称 */}
              <div className="dba-ai-gallery-publish-modal__tag-create-row">
                <span className="dba-ai-gallery-publish-modal__tag-create-label">
                  {formatMessage({ id: 'dba_ai_gallery_publish_tag_name_cn_label' })}
                </span>
                <div className="dba-ai-gallery-publish-modal__tag-input-wrap">
                  <SearchBox
                    value={customTagDraft.cn}
                    placeholder={formatMessage({
                      id: 'dba_ai_gallery_publish_tag_cn_placeholder',
                    })}
                    onChange={(val: string) => setCustomTagDraft({ cn: val.slice(0, TAG_CN_MAX_LENGTH) })}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        // 如果中英文都已填写，提交
                        if (customTagDraft.cn.trim() && customTagDraft.en.trim()) {
                          addCustomTag();
                        }
                      }
                    }}
                  />
                  {customTagDraft.cn.length >= TAG_CN_MAX_LENGTH && (
                    <div className="dba-ai-gallery-publish-modal__tag-error-tip">
                      {formatMessage({ id: 'dba_ai_gallery_publish_tag_cn_max_length_tip' })}
                    </div>
                  )}
                </div>
              </div>
              {/* 英文标签名称 */}
              <div className="dba-ai-gallery-publish-modal__tag-create-row">
                <span className="dba-ai-gallery-publish-modal__tag-create-label">
                  {formatMessage({ id: 'dba_ai_gallery_publish_tag_name_en_label' })}
                </span>
                <div className="dba-ai-gallery-publish-modal__tag-input-wrap">
                  <SearchBox
                    value={customTagDraft.en}
                    placeholder={formatMessage({
                      id: 'dba_ai_gallery_publish_tag_en_placeholder',
                    })}
                    onChange={(val: string) => setCustomTagDraft({ en: val.slice(0, TAG_EN_MAX_LENGTH) })}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        // 如果中英文都已填写，提交
                        if (customTagDraft.cn.trim() && customTagDraft.en.trim()) {
                          addCustomTag();
                        }
                      }
                    }}
                  />
                  {customTagDraft.en.length >= TAG_EN_MAX_LENGTH && (
                    <div className="dba-ai-gallery-publish-modal__tag-error-tip">
                      {formatMessage({ id: 'dba_ai_gallery_publish_tag_en_max_length_tip' })}
                    </div>
                  )}
                </div>
              </div>
              <div className="dba-ai-gallery-publish-modal__tag-create-actions">
                <Button
                  type="primary"
                  onClick={addCustomTag}
                  loading={createTagLoading}
                  disabled={!customTagDraft.cn.trim() || !customTagDraft.en.trim()}
                >
                  {formatMessage({ id: 'dba_confirm' })}
                </Button>
                <Button
                  type="weak"
                  onClick={() => {
                    setShowCustomTagInput(false);
                    setCustomTagDraft({ cn: '', en: '' });
                  }}
                >
                  {formatMessage({ id: 'dba_ai_gallery_publish_tag_back' })}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Dropdown>
    </div>
  );
};

/** 访问权限选项配置 */
const VISIBILITY_OPTIONS: Array<{
  value: TAiGalleryPublishVisibility;
  icon: string;
  activeIcon: string;
  muted?: boolean;
  titleKey: string;
  descKey: string;
  badge?: string;
}> = [
  {
    value: 'self',
    icon: iconAccessLock,
    activeIcon: iconAccessLockActive,
    titleKey: 'dba_ai_gallery_publish_visibility_self_title',
    descKey: 'dba_ai_gallery_publish_visibility_self_desc',
  },
  {
    value: 'user',
    icon: iconAccessPeople,
    activeIcon: iconAccessPeopleActive,
    muted: true,
    titleKey: 'dba_ai_gallery_publish_visibility_user_title',
    descKey: 'dba_ai_gallery_publish_visibility_user_desc',
  },
  {
    value: 'public',
    icon: iconAccessGlobe,
    activeIcon: iconAccessGlobeActive,
    muted: true,
    titleKey: 'dba_ai_gallery_publish_visibility_public_title',
    descKey: 'dba_ai_gallery_publish_visibility_public_desc',
    badge: 'dba_ai_gallery_publish_visibility_public_badge',
  },
];

export interface IAiGalleryPublishVisibilityFieldProps {
  value?: IAiGalleryPublishVisibilityPayload;
  onChange?: (v: IAiGalleryPublishVisibilityPayload) => void;
  /** 用户搜索回调（指定人员模式时使用） */
  onSearchUsers?: (keyword: string) => Promise<IAiGalleryUserItem[]>;
  /** 用户搜索加载中 */
  searchLoading?: boolean;
}

/**
 * 访问权限选择器：权限类型 + 指定用户
 * 
 * visibility=user 时，需要选择分享用户（存储 openid）
 */
export const AiGalleryPublishVisibilityField: React.FC<IAiGalleryPublishVisibilityFieldProps> = ({
  value,
  onChange,
  onSearchUsers,
  searchLoading = false,
}) => {
  const { formatMessage } = useIntl();
  const payload = normalizeAiGalleryPublishVisibilityPayload(value);
  const { visibility, shareUsers } = payload;

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // TagSelect 搜索结果选项
  const [userOptions, setUserOptions] = useState<Array<{ text: React.ReactNode; value: string; email?: string; }>>([]);

  const applyPayload = useCallback((nextPayload: IAiGalleryPublishVisibilityPayload): void => {
    onChangeRef.current?.(nextPayload);
  }, []);

  const currentOption = VISIBILITY_OPTIONS.find(opt => opt.value === visibility) || VISIBILITY_OPTIONS[0];

  // 搜索用户（供 TagSelect onSearch 使用）
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // 记录当前搜索关键词，用于判断下拉是否应打开
  const searchKeywordRef = useRef('');
  const handleSearchUsers = useCallback(
    (keyword: string) => {
      searchKeywordRef.current = keyword;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (!keyword || !onSearchUsers) {
        setUserOptions([]);
        return;
      }
      timerRef.current = setTimeout(async () => {
        const results = await onSearchUsers(keyword);
        // 防止过期的搜索结果覆盖（用户可能已清空输入）
        if (searchKeywordRef.current !== keyword) return;
        const options = (results || []).map((item) => ({
          text: item.email ? `${item.openid}(${item.email})` : item.openid,
          value: item.openid,
          email: item.email,
        }));
        setUserOptions(options);
      }, 300);
    },
    [onSearchUsers],
  );

  // TagSelect onChange：同步已选用户到 payload
  const handleTagSelectChange = useCallback(
    (selectedOpenids: string[]) => {
      const nextShareUsers = selectedOpenids.map((openid) => {
        // 尝试从已有 shareUsers 中复用信息
        const existing = shareUsers.find(u => u.openid === openid);
        if (existing) return existing;
        // 从搜索结果中查找
        return { openid, username: openid, email: '', avatar: '' };
      });
      applyPayload({ ...payload, shareUsers: nextShareUsers });
    },
    [applyPayload, payload, shareUsers],
  );

  const visibilityTriggerText = formatMessage({ id: `dba_ai_gallery_publish_visibility_trigger_${visibility}` });
  const [triggerTitle, triggerDesc] = visibilityTriggerText.split(' · ');

  const visibilityTrigger = (
    <button type="button" className="dba-ai-gallery-publish-modal__access-trigger">
      <img
        src={currentOption.activeIcon}
        alt=""
        className="dba-ai-gallery-publish-modal__access-ico"
        aria-hidden={true}
      />
      <span className="dba-ai-gallery-publish-modal__access-trigger-text">
        {triggerTitle}
        {triggerDesc && (
          <span className="dba-ai-gallery-publish-modal__access-trigger-desc">
            {' · '}
            {triggerDesc}
          </span>
        )}
      </span>
      <Icon type="arrowdown" />
    </button>
  );

  return (
    <div className="dba-ai-gallery-publish-modal__access-field-root">
      <Dropdown
        trigger="click"
        appearance="pure"
        matchButtonWidth
        overlayClassName="dba-ai-gallery-publish-modal__access-overlay"
        button={visibilityTrigger}
      >
        {(closeDropdown) => (
          <List type="option">
            {VISIBILITY_OPTIONS.map((opt) => (
              <List.Item
                key={opt.value}
                selected={visibility === opt.value}
                onClick={() => {
                  applyPayload({ ...payload, visibility: opt.value });
                  closeDropdown();
                }}
              >
                <div className={classNames('dba-ai-gallery-publish-modal__access-option', {
                  'dba-ai-gallery-publish-modal__access-option--public': opt.value === 'public',
                  'dba-ai-gallery-publish-modal__access-option--active': visibility === opt.value,
                })}>
                  <div className="dba-ai-gallery-publish-modal__access-ico-wrap">
                    <img
                      src={visibility === opt.value ? opt.activeIcon : opt.icon}
                      alt=""
                      className="dba-ai-gallery-publish-modal__access-ico"
                      aria-hidden={true}
                    />
                  </div>
                  <div>
                    {opt.badge ? (
                      <div className="dba-ai-gallery-publish-modal__access-title-row">
                        <span className="dba-ai-gallery-publish-modal__access-title">
                          {formatMessage({ id: opt.titleKey })}
                        </span>
                        <span className="dba-ai-gallery-publish-modal__access-badge">
                          {formatMessage({ id: opt.badge })}
                        </span>
                      </div>
                    ) : (
                      <div className="dba-ai-gallery-publish-modal__access-title">
                        {formatMessage({ id: opt.titleKey })}
                      </div>
                    )}
                    <div className="dba-ai-gallery-publish-modal__access-desc">
                      {formatMessage({ id: opt.descKey })}
                    </div>
                  </div>
                </div>
              </List.Item>
            ))}
          </List>
        )}
      </Dropdown>

      {visibility === 'user' && (
        <TagSelect
          className="dba-ai-gallery-publish-modal__user-tag-select"
          overlayClassName="dba-ai-gallery-publish-modal__user-tag-select-overlay"
          placeholder={formatMessage({ id: 'dba_ai_gallery_publish_share_users_hint' })}
          options={userOptions}
          optionsOnly
          filter={() => true}
          autoClearSearchValue
          value={shareUsers.map(u => u.openid)}
          onChange={handleTagSelectChange}
          onSearch={handleSearchUsers}
          tips={searchLoading ? <StatusTip status="loading" /> : null}
        />
      )}
    </div>
  );
};

// ==============================
// 数据类型选择器（真实数据 / Demo 数据）
// ==============================

const DATA_MODE_OPTIONS = [
  {
    value: 'demo' as const,
    labelKey: 'dba_ai_gallery_publish_data_mode_demo',
  },
  {
    value: 'real' as const,
    labelKey: 'dba_ai_gallery_publish_data_mode_real',
  },
];

export interface IAiGalleryPublishDataModeFieldProps {
  value?: TAiGalleryDataMode;
  onChange?: (mode: TAiGalleryDataMode) => void;
}

/**
 * 数据类型选择器：使用 tea Radio 组件（Demo 数据 / 真实数据）
 *
 * 默认选中 Demo 数据。
 */
export const AiGalleryPublishDataModeField: React.FC<IAiGalleryPublishDataModeFieldProps> = ({
  value = 'demo',
  onChange,
}) => {
  const { formatMessage } = useIntl();

  return (
    <div className="dba-ai-gallery-publish-modal__data-mode-field-root" style={{ verticalAlign: 'middle' }}>
      {DATA_MODE_OPTIONS.map((opt) => (
        <Radio
          key={opt.value}
          name="ai-gallery-data-mode"
          value={value === opt.value}
          onChange={() => onChange?.(opt.value)}
        >
          {formatMessage({ id: opt.labelKey })}
        </Radio>
      ))}
    </div>
  );
};
